import { Badge, Box, Button, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PessoaEncontrada, urlDoCracha } from '../api/lookup'
import { useEventConfig } from '../hooks/useEventConfig'
import api from '../utils/api'

const MotionBox = motion(Box)

type Etapa = 'conferir' | 'imprimindo' | 'confirmar' | 'concluido'

/** Volta ao início se o participante abandonar o totem no meio do fluxo. */
const INATIVIDADE_MS = 120_000

const PrintEtiqueta: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { config } = useEventConfig()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const pessoa: PessoaEncontrada | undefined = (location.state as any)?.pessoa
  const [etapa, setEtapa] = useState<Etapa>('conferir')
  const [tentativas, setTentativas] = useState(0)
  const [erro, setErro] = useState<string | null>(null)

  const primaria = config.branding?.primaryColor || '#092D77'
  const secundaria = config.branding?.secondaryColor || '#38B7B4'

  const voltar = useCallback(() => navigate('/autoatendimento'), [navigate])

  useEffect(() => {
    if (!pessoa) voltar()
  }, [pessoa, voltar])

  useEffect(() => {
    if (etapa === 'concluido') return
    const t = setTimeout(voltar, INATIVIDADE_MS)
    return () => clearTimeout(t)
  }, [etapa, tentativas, voltar])

  /**
   * Manda o crachá para a impressora.
   *
   * A folha vem renderizada pelo servidor, em milímetros e no tamanho físico da etiqueta.
   * Antes cada frontend desenhava a sua — eram cinco versões divergentes, e nenhuma
   * imprimia conselho profissional nem estado.
   */
  const imprimir = useCallback(async () => {
    if (!pessoa?.credentialCode) return
    setEtapa('imprimindo')
    setErro(null)

    const iframe = iframeRef.current
    if (!iframe) return

    try {
      // O HTML é buscado e injetado por `srcdoc`, não apontado por `src`.
      //
      // O crachá é servido pelo domínio da API, e o totem roda em outro domínio: um
      // iframe apontado para lá seria cross-origin e `contentWindow.print()` lançaria
      // SecurityError. Com `srcdoc` o documento herda a origem desta página e a
      // impressão é permitida. Funciona porque a folha é autossuficiente — CSS inline
      // e QR como SVG embutido, sem nenhum recurso externo a carregar.
      const resp = await fetch(urlDoCracha(pessoa.credentialCode))
      if (!resp.ok) throw new Error(String(resp.status))
      const html = await resp.text()

      await new Promise<void>(resolve => {
        iframe.onload = () => resolve()
        iframe.srcdoc = html
      })

      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
    } catch {
      setErro('Não foi possível preparar a impressão. Tente novamente.')
    }

    // Não gravamos nada aqui: o diálogo de impressão retorna igual com a impressora
    // sem etiqueta ou com o usuário cancelando. Quem confirma é a pessoa.
    setTentativas(n => n + 1)
    setEtapa('confirmar')
  }, [pessoa])

  /** Só a confirmação de que a etiqueta saiu registra a presença. */
  const confirmarEntrega = useCallback(async () => {
    if (!pessoa?.registrationUid) return
    try {
      await api.post('/checkin', { registration: pessoa.registrationUid, room: null })
    } catch {
      // A credencial física já está com a pessoa; o check-in é reconciliável depois.
      // Travar a fila do totem por causa disso seria pior que o registro faltante.
    }
    setEtapa('concluido')
    setTimeout(voltar, 4000)
  }, [pessoa, voltar])

  if (!pessoa) return null

  const detalhes = [pessoa.empresa, pessoa.conselhoProfissional, pessoa.estadoUf]
    .filter(Boolean)
    .join(' · ')

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      minH="100vh"
      bg={primaria}
      color="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px={6}
      py={8}
    >
      {/* Fora da tela: é a folha que vai para a impressora. */}
      <iframe
        ref={iframeRef}
        title="cracha"
        style={{ position: 'fixed', width: 0, height: 0, border: 0, visibility: 'hidden' }}
      />

      <VStack spacing={2} mb={8} textAlign="center" maxW="900px">
        <Heading fontSize={{ base: '3xl', md: '5xl' }}>{pessoa.nome}</Heading>
        {detalhes && (
          <Text fontSize={{ base: 'md', md: '2xl' }} opacity={0.9}>
            {detalhes}
          </Text>
        )}
        <HStack spacing={3} pt={2}>
          {pessoa.documentoMascarado && (
            <Badge fontSize={{ base: 'sm', md: 'md' }} colorScheme="blue">
              {pessoa.documentoMascarado}
            </Badge>
          )}
          {pessoa.jaCredenciado && (
            <Badge fontSize={{ base: 'sm', md: 'md' }} colorScheme="green">
              já credenciado
            </Badge>
          )}
        </HStack>
      </VStack>

      {etapa === 'conferir' && (
        <VStack spacing={4} w={{ base: '95%', md: '620px' }}>
          <Text fontSize={{ base: 'lg', md: '2xl' }} textAlign="center">
            Confira se é você e imprima sua credencial.
          </Text>
          <Button
            onClick={imprimir}
            w="100%"
            h={{ base: '90px', md: '120px' }}
            fontSize={{ base: '2xl', md: '4xl' }}
            bg={secundaria}
            color="white"
            _hover={{ bg: secundaria }}
          >
            Imprimir credencial
          </Button>
          <Button onClick={voltar} variant="outline" color="white" borderColor="whiteAlpha.600" size="lg">
            Não sou eu — voltar
          </Button>
        </VStack>
      )}

      {etapa === 'imprimindo' && (
        <VStack spacing={4}>
          <Spinner size="xl" thickness="5px" />
          <Text fontSize={{ base: 'xl', md: '3xl' }}>Enviando para a impressora…</Text>
        </VStack>
      )}

      {etapa === 'confirmar' && (
        <VStack spacing={4} w={{ base: '95%', md: '620px' }}>
          <Text fontSize={{ base: 'xl', md: '3xl' }} textAlign="center" fontWeight="600">
            A etiqueta saiu corretamente?
          </Text>
          {erro && <Text color="red.200">{erro}</Text>}
          <Button
            onClick={confirmarEntrega}
            w="100%"
            h={{ base: '90px', md: '110px' }}
            fontSize={{ base: '2xl', md: '3xl' }}
            colorScheme="green"
          >
            Sim, peguei minha credencial
          </Button>
          <Button
            onClick={imprimir}
            w="100%"
            h={{ base: '76px', md: '90px' }}
            fontSize={{ base: 'xl', md: '2xl' }}
            variant="outline"
            color="white"
            borderColor="whiteAlpha.700"
            _hover={{ bg: 'whiteAlpha.200' }}
          >
            Não saiu — imprimir de novo
          </Button>
          {tentativas > 2 && (
            <Text fontSize={{ base: 'sm', md: 'md' }} opacity={0.85} textAlign="center">
              A impressora pode estar sem etiqueta. Procure a secretaria do evento.
            </Text>
          )}
        </VStack>
      )}

      {etapa === 'concluido' && (
        <VStack spacing={3}>
          <Heading fontSize={{ base: '2xl', md: '4xl' }}>Pronto!</Heading>
          <Text fontSize={{ base: 'lg', md: '2xl' }} textAlign="center">
            Bom evento. Guarde sua credencial — ela dá acesso às salas.
          </Text>
        </VStack>
      )}
    </MotionBox>
  )
}

export default PrintEtiqueta
